import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  ArrowLeftRight,
  Coins,
  Copy,
  Eye,
  FilePen,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Lock,
  MonitorSmartphone,
  Plus,
  Rocket,
  RotateCcw,
  Save,
  ScrollText,
  Search,
  ServerCog,
  ShieldCheck,
  Siren,
  Sparkles,
  UserRoundCog,
  UserSearch,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { permissionsOf } from '@shared/permissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  SortableHead,
  TableCard,
  Toolbar,
} from '@/components/shell'
import {
  api,
  ApiError,
  type Paginated,
  type RoleMenuPolicies,
  type RoleMenuPolicy,
  type UserRow,
} from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { useCan, useMe } from '@/lib/session'
import { cn } from '@/lib/utils'
import { roleLabel } from '../dashboard'

export const Route = createFileRoute('/dashboard/roles')({
  component: RolesPage,
})

type Role = RoleMenuPolicy['role']

const ROLE_META: Record<Role, { description: string; icon: LucideIcon }> = {
  admin: { description: '系统配置与全量管理', icon: ShieldCheck },
  operator: { description: '日常运营、SuperNova 资格与风控处置', icon: Wrench },
  campaign_operator: { description: 'SuperNova 报名及代币资格、Telegram 通知', icon: Rocket },
  viewer: { description: '只读查看，不执行写操作', icon: Eye },
}

const ROLE_TONE: Record<string, string> = {
  admin: 'bg-cyan/15 text-cyan hover:bg-cyan/15',
  operator: 'bg-edge/12 text-txt hover:bg-edge/12',
  campaign_operator: 'bg-cyan/10 text-cyan hover:bg-cyan/10',
  viewer: 'bg-edge/8 text-mut hover:bg-edge/8',
}

const MENU_ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  Coins,
  FilePen,
  ArrowLeftRight,
  Wallet,
  UserSearch,
  ShieldCheck,
  MonitorSmartphone,
  Activity,
  ScrollText,
  ListChecks,
  Landmark,
  ServerCog,
  Siren,
  Sparkles,
  UserRoundCog,
  Rocket,
}

const MENU_DESCRIPTIONS: Record<string, string> = {
  overview: '查看全局经营数据与核心指标',
  tokens: '浏览代币列表、状态与详情',
  'meta-proposals': '审核非创建者提交的代币资料修改申请',
  trades: '检索链上成交与交易明细',
  wallets: '查看发行人画像与关联代币',
  traders: '查看交易者活动与持仓估算',
  roles: '配置角色菜单并分配成员角色',
  sessions: '查看当前有效的后台登录会话',
  'system-logs': '排查认证、接口与系统运行异常',
  audit: '追溯不可篡改的后台操作流水',
  'launch-vanity': '配置 CREATE2 地址后缀与代币名称敏感词',
  'contract-lists': '追加第三方检测池与合约免税端点',
  // 状态部分对所有角色开放（contract:read），页面内的写操作另由 contract:manage 收口
  'contract-manager': '查看 Manager V4 LP 默认费率、所有权、签名人与紧急开关',
  'contract-ownership': '五个 Ownable2Step 合约的两步式所有权交接与 Safe calldata 导出',
  'contract-treasury': '内盘手续费与 LP 平台份额的余额统计、collect/harvest 与提取',
  operations: '查看部署一致性、索引管道、运行时配置与回购/报价流水',
  alerts: '查看并配置通用告警规则',
  supernova: '查看 SuperNova 活动状态与三榜，配置活动、处理报名资格',
}

/**
 * 功能权限的中文说明。
 *
 * 权限清单本身来自 `@shared/permissions` 的 `permissionsOf()`，不在前端复制矩阵——
 * 这里只负责把 `resource:action` 翻成人话。矩阵新增动作时这张表没跟上，
 * 会原样显示 key，不会漏项。
 */
const PERMISSION_LABELS: Record<string, string> = {
  'token:read': '查看代币',
  'token:hide': '下架代币',
  'token:unhide': '恢复代币',
  'token:hot': '标记热门',
  'token:reviewMeta': '审核资料申请（含删除）',
  'wallet:read': '查看地址画像',
  'wallet:bulkHide': '批量下架',
  'user:read': '查看成员',
  'user:create': '创建成员账号',
  'user:setRole': '分配角色',
  'user:ban': '停用账号',
  'user:revokeSession': '强制下线',
  'auditLog:read': '查看审计日志',
  'systemLog:read': '查看系统日志',
  'contract:read': '查看合约状态',
  'contract:manage': '管理合约名单与全局控制',
  'operations:read': '查看运维数据',
  'operations:manage': '管理自动回购开关与税收分红派发阈值',
  'alertRule:read': '查看告警规则',
  'alertRule:manage': '配置告警规则',
  'notify:read': '查看 Telegram 配置与记录',
  'notify:send': '试发与重发 Telegram 通知',
  'notify:manage': '管理 Telegram Bot、群与模板',
  'supernova:read': '查看 SuperNova 活动与榜单',
  'supernova:manageRegistrations': '处理 SuperNova 报名资格',
  'supernova:manageTokenEligibility': '处理 SuperNova 代币资格',
  'supernova:manage': '配置 SuperNova 活动、快照与终榜',
}

function RolesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['role-menu-policies'],
    queryFn: () => api.get<RoleMenuPolicies>('/roles'),
  })

  return (
    <Page>
      <PageHeader
        title="角色管理"
        description="左侧选择角色，右侧配置该角色能看到的菜单入口；成员分配负责把人放进角色。"
      />

      <Tabs defaultValue="menus" className="gap-5">
        <TabsList variant="line" className="h-10 gap-5 border-b border-line/50 px-0">
          <TabsTrigger value="menus" className="h-9 px-0 text-[13px]">
            <ShieldCheck className="size-4" />
            菜单权限
          </TabsTrigger>
          <TabsTrigger value="members" className="h-9 px-0 text-[13px]">
            <Users className="size-4" />
            成员分配
          </TabsTrigger>
        </TabsList>

        <TabsContent value="menus">
          {isLoading || !data ? <PermissionsSkeleton /> : <MenuPermissions data={data} />}
        </TabsContent>

        <TabsContent value="members">
          <MemberAssignments />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

function PermissionsSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[17rem_1fr]">
      <div className="space-y-4">
        <Skeleton className="h-[13.5rem] rounded-apple-lg" />
        <Skeleton className="h-40 rounded-apple-lg" />
      </div>
      <Skeleton className="h-[32rem] rounded-apple-lg" />
    </div>
  )
}

/**
 * 菜单权限：左选角色、右配菜单的主从布局。
 *
 * 改动保存在 `drafts` 里而不是直接改 `selected` 状态，有两个原因：
 *   1. 后台刷新（保存后的 invalidate、手动 refetch）不会把没保存的编辑冲掉；
 *   2. 切到别的角色再切回来，编辑还在——误点角色不再等于丢失修改。
 * 代价是「哪些角色有未保存改动」必须显式提示，所以左栏每个角色带一个警示点。
 */
function MenuPermissions({ data }: { data: RoleMenuPolicies }) {
  const qc = useQueryClient()
  const [activeRole, setActiveRole] = useState<Role>('admin')
  const [drafts, setDrafts] = useState<Partial<Record<Role, string[]>>>({})

  const active = data.roles.find((item) => item.role === activeRole) ?? data.roles[0]!
  const selected = drafts[active.role] ?? active.enabledMenuIds

  const changes = useMemo(
    () => diffMenus(active.enabledMenuIds, selected),
    [active.enabledMenuIds, selected],
  )
  const dirty = changes.added.length + changes.removed.length > 0

  const visibleCount = selected.filter((id) => active.availableMenuIds.includes(id)).length

  const setSelected = (next: string[]) =>
    setDrafts((current) => ({ ...current, [active.role]: next }))

  const clearDraft = (role: Role) =>
    setDrafts((current) => {
      const next = { ...current }
      delete next[role]
      return next
    })

  const save = useMutation({
    mutationFn: () =>
      api.patch<Pick<RoleMenuPolicy, 'role' | 'enabledMenuIds' | 'availableMenuIds'>>(
        `/roles/${active.role}/menus`,
        { menuIds: selected },
      ),
    onSuccess: (result) => {
      /**
       * 先把结果打进缓存再 invalidate：只清缓存的话，草稿已经丢弃而
       * `active.enabledMenuIds` 还是旧值，重新拉到之前面板会闪一下旧配置。
       */
      qc.setQueryData<RoleMenuPolicies>(['role-menu-policies'], (prev) =>
        prev
          ? {
              ...prev,
              roles: prev.roles.map((item) =>
                item.role === result.role
                  ? {
                      ...item,
                      enabledMenuIds: result.enabledMenuIds,
                      availableMenuIds: result.availableMenuIds,
                    }
                  : item,
              ),
            }
          : prev,
      )
      clearDraft(result.role)
      toast.success(`${roleLabel(result.role)}的菜单权限已保存`)
      qc.invalidateQueries({ queryKey: ['role-menu-policies'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (error) =>
      toast.error('保存失败', {
        description: error instanceof ApiError ? error.message : '请稍后重试',
      }),
  })

  const groups = useMemo(() => {
    return data.menus.reduce<Record<string, typeof data.menus>>((acc, menu) => {
      ;(acc[menu.group] ??= []).push(menu)
      return acc
    }, {})
  }, [data.menus])

  const permissions = permissionsOf(active.role)

  return (
    <div className="grid gap-4 lg:h-[calc(100vh-17rem)] lg:min-h-[30rem] lg:grid-cols-[17rem_1fr]">
      {/* ── 左栏：角色选择 + 该角色的功能权限 ───────────────────────────── */}
      <div className="flex min-h-0 flex-col gap-4">
        <div className="surface shrink-0 p-2" role="radiogroup" aria-label="选择角色">
          {data.roles.map((item) => {
            const meta = ROLE_META[item.role]
            const Icon = meta.icon
            const on = item.role === active.role
            const unsaved = !!drafts[item.role] && !sameIds(drafts[item.role]!, item.enabledMenuIds)

            return (
              <button
                key={item.role}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setActiveRole(item.role)}
                className={cn(
                  'pressable flex w-full items-center gap-3 rounded-apple px-3 py-2.5 text-left',
                  on
                    ? // 浅底上仅靠 10% 的色块不够"实"，补一圈同色细描边把选中项钉住
                      'bg-cyan/10 inset-ring inset-ring-cyan/20'
                    : 'hover:bg-edge/[0.055]',
                )}
              >
                <div
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-apple-sm',
                    on ? 'bg-cyan/15 text-cyan' : 'bg-edge/10 text-mut2',
                  )}
                >
                  <Icon className="size-4" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'truncate text-[13.5px]',
                        on ? 'font-medium text-cyan' : 'text-txt',
                      )}
                    >
                      {roleLabel(item.role)}
                    </span>
                    {unsaved && (
                      <span
                        aria-label="有未保存的修改"
                        title="有未保存的修改"
                        className="size-1.5 shrink-0 rounded-full bg-warn"
                      />
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-mut2">{meta.description}</div>
                </div>

                <span className="tnum shrink-0 font-mono text-[11.5px] text-mut2">
                  {item.memberCount} 人
                </span>
              </button>
            )
          })}
        </div>

        {/*
          功能权限面板。这里不是装饰——右侧那些带锁的菜单之所以点不开，
          原因就写在这张清单里，两者放在同一屏才解释得通。
        */}
        <div className="surface shrink-0 overflow-hidden p-0">
          <div className="section-label border-b border-line/40 px-4 py-3">功能权限</div>
          <div className="p-4">
            <div className="flex flex-wrap gap-1.5">
              {permissions.map((key) => (
                <span
                  key={key}
                  className="rounded-full bg-edge/10 px-2 py-0.5 text-[11px] text-mut"
                >
                  {PERMISSION_LABELS[key] ?? key}
                </span>
              ))}
            </div>
            <p className="mt-3.5 text-[11px] leading-relaxed text-mut2">
              功能权限由系统矩阵固定，不在此处调整。菜单权限只能在它的范围内收窄可见入口，
              无法放大角色的实际能力。
            </p>
          </div>
        </div>
      </div>

      {/* ── 右栏：菜单开关。列表自己滚动，操作栏常驻底部 ────────────────── */}
      <section className="surface flex min-h-0 flex-col overflow-hidden p-0">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line/50 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <h2 className="text-[14.5px] font-medium tracking-tight text-txt">
              {roleLabel(active.role)}的可见菜单
            </h2>
            <span className="tnum font-mono text-[11.5px] text-mut2">
              {visibleCount}/{active.availableMenuIds.length}
            </span>
          </div>
          <p className="text-[11.5px] text-mut2">关闭只隐藏入口，不改变功能权限</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {Object.entries(groups).map(([group, menus]) => {
            const available = menus.filter((menu) => active.availableMenuIds.includes(menu.id))
            const toggleable = available.filter((menu) => menu.configurable)
            const allOn = toggleable.every((menu) => selected.includes(menu.id))
            const onCount = available.filter((menu) => selected.includes(menu.id)).length

            return (
              <div key={group} className="border-b border-line/35 last:border-b-0">
                {/* 分组带走 bg-void：CLAUDE.md 的层次约定里，"凹陷"两个主题下都用页面底色 */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-void/95 px-5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="section-label">{group}</span>
                    <span className="tnum font-mono text-[10.5px] text-dim">
                      {onCount}/{available.length}
                    </span>
                  </div>

                  {toggleable.length > 0 && (
                    <button
                      type="button"
                      className="pressable rounded-apple-sm px-2 py-1 text-[11.5px] text-mut2 hover:bg-edge/10 hover:text-txt"
                      onClick={() => {
                        const ids = toggleable.map((menu) => menu.id)
                        setSelected(
                          allOn
                            ? selected.filter((id) => !ids.includes(id))
                            : [...new Set([...selected, ...ids])],
                        )
                      }}
                    >
                      {allOn ? '全部关闭' : '全部开启'}
                    </button>
                  )}
                </div>

                <div className="divide-y divide-line/30">
                  {menus.map((menu) => (
                    <MenuRow
                      key={menu.id}
                      menu={menu}
                      role={active.role}
                      available={active.availableMenuIds.includes(menu.id)}
                      checked={selected.includes(menu.id)}
                      onChange={(next) =>
                        setSelected(
                          next
                            ? [...new Set([...selected, menu.id])]
                            : selected.filter((id) => id !== menu.id),
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line/50 px-5 py-3">
          <div className="min-w-0 text-[11.5px] text-mut2">
            {dirty ? (
              <span className="text-warn">{describeChanges(changes)}，尚未保存</span>
            ) : active.updatedAt ? (
              <span className="tnum">上次保存 {formatDateTime(active.updatedAt)}</span>
            ) : (
              '当前为系统默认配置'
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="pressable h-8 rounded-apple-sm text-[12.5px]"
              disabled={!dirty || save.isPending}
              onClick={() => clearDraft(active.role)}
            >
              <RotateCcw className="size-3.5" />
              放弃
            </Button>
            <Button
              size="sm"
              className="pressable h-8 rounded-apple text-[12.5px]"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              <Save className="size-3.5" />
              {save.isPending ? '保存中…' : '保存更改'}
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

/**
 * 单个菜单行。三种形态共用一套行高与栅格，切换角色时列表不会跳。
 *
 * 不可用的行**不用 opacity**：半透明会把文字洗进背景，浅底上尤其明显，
 * 而这里恰恰是最需要读清楚的一句（为什么点不了）。改用中性灰 + 锁图标。
 */
function MenuRow({
  menu,
  role,
  available,
  checked,
  onChange,
}: {
  menu: RoleMenuPolicies['menus'][number]
  role: string
  available: boolean
  checked: boolean
  onChange: (next: boolean) => void
}) {
  const Icon = MENU_ICONS[menu.icon] ?? LayoutDashboard
  const editable = available && menu.configurable
  const Row = editable ? 'label' : 'div'
  const chip = 'shrink-0 rounded-full bg-edge/10 px-2 py-0.5 font-mono text-[10px] text-mut2'

  return (
    <Row
      className={cn('flex items-center gap-3 px-5 py-2.5', editable && 'row-hover cursor-pointer')}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-apple-sm',
          !available
            ? 'bg-edge/[0.06] text-dim'
            : checked
              ? 'bg-cyan/12 text-cyan'
              : 'bg-edge/10 text-mut2',
        )}
      >
        {available ? <Icon className="size-4" /> : <Lock className="size-3.5" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className={cn('text-[13px]', available ? 'text-txt' : 'text-mut')}>{menu.title}</div>
        <div className="mt-0.5 truncate text-[11px] text-mut2">
          {available ? MENU_DESCRIPTIONS[menu.id] : '该角色没有对应的功能权限，无法开启'}
        </div>
      </div>

      {!available ? (
        <span className={chip}>无权限</span>
      ) : !menu.configurable ? (
        <span className={chip}>系统固定</span>
      ) : (
        <Switch
          checked={checked}
          aria-label={`${roleLabel(role)} ${menu.title}`}
          onCheckedChange={onChange}
        />
      )}
    </Row>
  )
}

function sameIds(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((id) => b.includes(id))
}

function diffMenus(base: readonly string[], next: readonly string[]) {
  const before = new Set(base)
  const after = new Set(next)
  return {
    added: next.filter((id) => !before.has(id)),
    removed: base.filter((id) => !after.has(id)),
  }
}

function describeChanges({ added, removed }: { added: string[]; removed: string[] }) {
  return [added.length && `开启 ${added.length} 项`, removed.length && `关闭 ${removed.length} 项`]
    .filter(Boolean)
    .join(' · ')
}

function MemberAssignments() {
  const can = useCan()
  const me = useMe()
  const qc = useQueryClient()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('all')
  const [status, setStatus] = useState('all')
  const [sort, setSort] = useState<string | undefined>()
  const [banTarget, setBanTarget] = useState<UserRow | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState<'campaign_operator' | 'viewer'>('campaign_operator')
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users', { page, search, role, status, sort }],
    queryFn: () =>
      api.get<Paginated<UserRow>>('/users', {
        page,
        perPage: 20,
        search: search || undefined,
        role: role === 'all' ? undefined : role,
        status: status === 'all' ? undefined : status,
        sort,
      }),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['users'] })
    qc.invalidateQueries({ queryKey: ['sessions'] })
    qc.invalidateQueries({ queryKey: ['role-menu-policies'] })
  }
  const onError = (error: unknown) =>
    toast.error('操作失败', {
      description: error instanceof ApiError ? error.message : '请稍后重试',
    })

  const createUser = useMutation({
    mutationFn: () => api.post<{ ok: true; user: { username: string; password: string } }>('/users', {
      username: newUsername.trim(),
      name: newName.trim(),
      role: newRole,
    }),
    onSuccess: (result) => {
      setCreated(result.user)
      invalidate()
      toast.success('成员账号已创建')
    },
    onError,
  })

  const closeCreate = (open: boolean) => {
    setCreateOpen(open)
    if (!open) {
      setCreated(null)
      createUser.reset()
      setNewUsername('')
      setNewName('')
      setNewRole('campaign_operator')
    }
  }

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch(`/users/${id}/role`, { role }),
    onSuccess: () => {
      toast.success('成员角色已更新')
      invalidate()
    },
    onError,
  })

  const unban = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/unban`),
    onSuccess: () => {
      toast.success('成员账号已启用')
      invalidate()
    },
    onError,
  })

  return (
    <div className="space-y-4">
      <Toolbar trailing={<div className="flex items-center gap-3">
        <Count value={data?.total} unit="人" />
        {can('user', 'create') && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 size-4" />创建账号
          </Button>
        )}
      </div>}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="姓名 / 账号 / 邮箱"
            className="h-9 w-60 rounded-apple pl-9 text-[13px]"
          />
        </div>

        <Select
          value={role}
          onValueChange={(value) => {
            setRole(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-32 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部角色</SelectItem>
            <SelectItem value="admin">超级管理员</SelectItem>
            <SelectItem value="operator">运营</SelectItem>
            <SelectItem value="campaign_operator">活动运营</SelectItem>
            <SelectItem value="viewer">只读</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="active">启用</SelectItem>
            <SelectItem value="disabled">停用</SelectItem>
          </SelectContent>
        </Select>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <SortableHead field="name" sort={sort} onSort={setSort} className="pl-6">
                成员
              </SortableHead>
              <TableHead className="section-label">联系方式</TableHead>
              <SortableHead field="role" sort={sort} onSort={setSort}>
                角色
              </SortableHead>
              <TableHead className="section-label">状态</TableHead>
              <SortableHead field="lastLoginAt" sort={sort} onSort={setSort}>
                最后登录
              </SortableHead>
              <TableHead className="section-label pr-6 text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 6 }, (_, index) => (
                <TableRow key={index} className="border-line/40">
                  <TableCell colSpan={6} className="px-6 py-3">
                    <Skeleton className="h-8 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={6}>
                  <EmptyState icon={Users} title="没有符合条件的成员" />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((user) => {
              const isSelf = user.id === me?.user.id
              const canSetRole = can('user', 'setRole') && !isSelf

              return (
                <TableRow
                  key={user.id}
                  /**
                   * 停用行用极淡的 rose 底而不是 opacity——半透明会把整行文字
                   * 一起洗淡，而停用原因恰恰是这一行最该读清楚的信息。
                   */
                  className={cn('row-hover border-line/40', user.banned && 'bg-rose/[0.03]')}
                >
                  <TableCell className="pl-6">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-edge/10 text-[12px] text-mut">
                        {user.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[13.5px] text-txt">{user.name}</span>
                          {isSelf && (
                            <Badge className="h-4 bg-cyan/15 px-1.5 text-[10px] text-cyan hover:bg-cyan/15">
                              你
                            </Badge>
                          )}
                        </div>
                        <div className="truncate font-mono text-[11px] text-mut2">
                          {user.username ?? '—'}
                        </div>
                      </div>
                    </div>
                  </TableCell>

                  {/* 邮箱与 Lark 绑定合成一列：Lark 只有两种取值，占一整列不值 */}
                  <TableCell className="max-w-[15rem]">
                    <div className="truncate text-[12.5px] text-mut">{user.email ?? '—'}</div>
                    <div
                      className={cn(
                        'mt-0.5 font-mono text-[10.5px]',
                        user.larkBound ? 'text-mut2' : 'text-dim',
                      )}
                    >
                      Lark {user.larkBound ? '已绑定' : '未绑定'}
                    </div>
                  </TableCell>

                  {/*
                    有权限时这一格就是下拉框本身——它已经显示了当前角色，
                    再配一个只读徽章等于同一个值在一行里出现两次。
                  */}
                  <TableCell>
                    {canSetRole ? (
                      <Select
                        value={user.role}
                        disabled={changeRole.isPending}
                        onValueChange={(nextRole) =>
                          changeRole.mutate({ id: user.id, role: nextRole })
                        }
                      >
                        <SelectTrigger className="h-8 w-[7.5rem] rounded-apple-sm text-[12px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-apple">
                          <SelectItem value="admin">超级管理员</SelectItem>
                          <SelectItem value="operator">运营</SelectItem>
                          <SelectItem value="campaign_operator">活动运营</SelectItem>
                          <SelectItem value="viewer">只读</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={cn('h-5 text-[10.5px]', ROLE_TONE[user.role])}>
                        {roleLabel(user.role)}
                      </Badge>
                    )}
                  </TableCell>

                  <TableCell>
                    {user.banned ? (
                      <div className="min-w-0">
                        <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                          停用
                        </Badge>
                        {user.banReason && (
                          <div
                            title={user.banReason}
                            className="mt-1 max-w-[11rem] truncate text-[10.5px] text-mut2"
                          >
                            {user.banReason}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[12px] text-mut">
                        <span aria-hidden className="size-1.5 rounded-full bg-mut2/60" />
                        启用
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="tnum font-mono text-[11px] text-mut2">
                    {formatDateTime(user.lastLoginAt)}
                  </TableCell>

                  <TableCell className="pr-6">
                    <div className="flex items-center justify-end">
                      {can('user', 'ban') &&
                        !isSelf &&
                        (user.banned ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="pressable h-8 rounded-apple-sm text-[12.5px]"
                            disabled={unban.isPending}
                            onClick={() => unban.mutate(user.id)}
                          >
                            启用
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="pressable h-8 rounded-apple-sm text-[12.5px] text-rose hover:bg-rose/10 hover:text-rose"
                            onClick={() => setBanTarget(user)}
                          >
                            停用
                          </Button>
                        ))}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />

      <Dialog open={createOpen} onOpenChange={(open) => { if (!createUser.isPending) closeCreate(open) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{created ? '账号已创建' : '创建成员账号'}</DialogTitle>
            <DialogDescription>
              {created
                ? '初始密码只在这里显示一次。请安全地交给成员，并提醒其登录后修改密码。'
                : '由超级管理员创建账号。活动运营可处理 SuperNova 报名与代币资格，并查看及发送 Telegram 通知。'}
            </DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-3">
              <div className="space-y-1"><Label>登录账号</Label><Input readOnly value={created.username} className="font-mono" /></div>
              <div className="space-y-1"><Label>初始密码</Label><Input readOnly value={created.password} className="font-mono" /></div>
              <Button variant="outline" onClick={() => {
                void navigator.clipboard.writeText(`${created.username}\n${created.password}`)
                  .then(() => toast.success('账号和密码已复制'))
                  .catch(() => toast.error('复制失败，请手动选择'))
              }}><Copy className="mr-1 size-4" />复制账号和密码</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-member-name">姓名</Label>
                <Input id="new-member-name" value={newName} maxLength={50} onChange={(event) => setNewName(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-member-username">登录账号</Label>
                <Input id="new-member-username" value={newUsername} maxLength={30} autoCapitalize="none" onChange={(event) => setNewUsername(event.target.value)} placeholder="3–30 位字母、数字、下划线或点号" />
              </div>
              <div className="space-y-1.5">
                <Label>角色</Label>
                <Select value={newRole} onValueChange={(value) => setNewRole(value as typeof newRole)}>
                  <SelectTrigger aria-label="新账号角色"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="campaign_operator">活动运营 · SuperNova 资格处理、Telegram 通知</SelectItem>
                    <SelectItem value="viewer">只读</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            {created ? (
              <Button onClick={() => closeCreate(false)}>完成</Button>
            ) : (
              <Button
                disabled={createUser.isPending || !newName.trim() || !/^[a-zA-Z0-9_.]{3,30}$/.test(newUsername.trim())}
                onClick={() => createUser.mutate()}
              >
                {createUser.isPending ? '创建中…' : '创建并生成密码'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BanDialog target={banTarget} onClose={() => setBanTarget(null)} onDone={invalidate} />
    </div>
  )
}

/**
 * 停用确认。
 *
 * 停用会连带吊销该成员的全部会话，属于「点一下就把人挡在门外」的操作，
 * 不该只有一个裸按钮。顺带把原因交给操作者填——原来固定写死「管理员停用」，
 * 审计流水里每条都一样，等于没记。
 */
function BanDialog({
  target,
  onClose,
  onDone,
}: {
  target: UserRow | null
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (target) setReason('')
  }, [target?.id])

  const ban = useMutation({
    mutationFn: () =>
      api.post(`/users/${target!.id}/ban`, { reason: reason.trim() || '管理员停用' }),
    onSuccess: () => {
      toast.success('成员账号已停用', { description: '该成员的全部会话已同时吊销' })
      onDone()
      onClose()
    },
    onError: (error: unknown) =>
      toast.error('操作失败', {
        description: error instanceof ApiError ? error.message : '请稍后重试',
      }),
  })

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>停用 {target?.name} 的账号</DialogTitle>
          <DialogDescription className="text-[12.5px] leading-relaxed">
            该成员将立即无法登录，现有的全部登录会话会被一并吊销。操作写入审计流水，之后可以随时重新启用。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label className="text-[12.5px]">停用原因</Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="会写进审计记录。留空则记为「管理员停用」"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button variant="destructive" disabled={ban.isPending} onClick={() => ban.mutate()}>
            {ban.isPending ? '处理中…' : '确认停用'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
